import React, { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';

import { Link } from 'formlabs-web-components/Link';
import { DisplayComponent } from 'formlabs-web-components/BaseComponents';
import { GridContainer, Grid, Cell } from 'formlabs-web-components/Grid';
import { Eyebrow, Header } from 'formlabs-web-components/Type';

import Card from '../../../../common/components/content/Card';
import { TableOfContents } from '../../../../common/components/content';
import { compareWithLocale } from '../../../../common/utils';

import LocaleContext from '../../../../common/context/locale';
import { format } from 'date-fns';

import SocialShareButtons from './SocialShareButtons';
import './Blog.module.css';

const BlogPost = ({
  title,
  children,
  category,
  pubDate,
  type,
  hideDate,
  url,
  relatedPosts,
  seoImage
}) => {
  const { t, i18n } = useTranslation();
  const { dateLocale } = useContext(LocaleContext);
  const localDateTime = format(new Date(pubDate), 'MMM dd, yyyy', {
    locale: dateLocale
  });

  const [readTime, setReadTime] = useState(null);
  if (relatedPosts)
    relatedPosts.sort((a, b) =>
      compareWithLocale(a.title, b.title, i18n.language)
    );
  // calculate read time
  useEffect(
    () => {
      const textPlugins = document.getElementsByClassName('text-plugin');
      const text = [...textPlugins].reduce(
        (result, element) => result + element.innerText,
        ''
      );
      const wordCount = text.split(' ').length;

      let minutes = Math.floor(parseFloat(wordCount / 200));
      let seconds = ((wordCount / 200).toFixed(2) + '').split('.');
      seconds = Math.floor(parseFloat(seconds[1] * 0.6).toFixed(2));

      if (seconds > 30) {
        minutes++;
      }
      if (minutes < 1) {
        minutes++;
        minutes = '< ' + minutes;
      }
      setReadTime(minutes);
    },
    [children]
  );

  const refined_url =
    url.slice(0, 6) === '/blog/' ? '/blog/' : `/${url.split('/')[1]}/blog/`;

  return (
    <div styleName="single-post-wrapper">
      <Helmet>
        {seoImage && <meta itemProp="image" content={seoImage} />}
        {seoImage && <meta name="twitter:image:src" content={seoImage} />}
        {seoImage && <meta property="og:image" content={seoImage} />}
      </Helmet>
      <GridContainer>
        <Grid direction="x">
          <Cell largeSize={24}>
            <Link to={refined_url} styleName="all-posts">
              <i className="fl-arrow-left" />
              {t('All Posts')}
            </Link>
            <div styleName="post-type">
              <Eyebrow>{type}</Eyebrow>
            </div>
          </Cell>
          <Cell largeSize={18}>
            <Header level={1} displayAs={1} weight="600">
              {title}
            </Header>
          </Cell>
          <Cell largeSize={6}>
            <SocialShareButtons title={title} type={type} url={url} />
          </Cell>
        </Grid>
      </GridContainer>
      <GridContainer>
        {((!hideDate && pubDate) || category || type || readTime) && (
          <div styleName="byline">
            {!hideDate && (
              <div styleName="item">
                <div styleName="icon">
                  <i className="fl-calendar" />
                </div>
                <div styleName="label">{localDateTime}</div>
              </div>
            )}
            {category && (
              <div styleName="item">
                <div styleName="icon">
                  <i className="fl-offer" />
                </div>
                <div styleName="label">{category}</div>
              </div>
            )}
            {type && (
              <div styleName="item">
                <div styleName="icon">
                  <i className="fl-folder_open" />
                </div>
                <div styleName="label">{type}</div>
              </div>
            )}
            {readTime && (
              <div styleName="item">
                <div styleName="icon">
                  <i className="fl-schedule" />
                </div>
                <div styleName="label">
                  {t('{{count}} minute read', {
                    count: readTime
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </GridContainer>
      <TableOfContents levels={[2, 3]}>{children}</TableOfContents>
      <GridContainer>
        <Grid direction="x" primaryAlignment="align-left">
          {relatedPosts.length > 0 && (
            <Cell smallSize={24}>
              <div styleName="related-posts">
                <div styleName="related-posts-header">
                  <Header level={3} displayAs={4}>
                    {t('Related Posts')}
                  </Header>
                </div>
                <Grid direction="x" gutters="padding">
                  {relatedPosts.map(post => (
                    <Cell mediumSize={8} key={post.title}>
                      <Card
                        image={post.listing_image}
                        imagePosition="top"
                        url={post.url}
                        eyebrowText={post.category.name}
                        title={post.title}
                        titleStyle="thin"
                        internalPadding="none"
                        hover="none"
                      />
                    </Cell>
                  ))}
                </Grid>
              </div>
            </Cell>
          )}
        </Grid>
      </GridContainer>
    </div>
  );
};

export default DisplayComponent(BlogPost);
